// ---------------------------------------------------------------------------
// Fallback Overview pieces for a grader whose type the client doesn't model yet
// (no descriptor). Rather than hiding the grader's configuration:
//   * FallbackOverviewNotice — an info MessageBar, rendered at the TOP of the
//     Overview so the "not fully supported" context is the first thing seen.
//   * FallbackOverviewSections — the persisted spec's `init` / `invoke` values,
//     read-only, rendered in the type-specific cards slot.
// Read-only always: the schema is unknown, so we never risk corrupting it.
// ---------------------------------------------------------------------------

import {
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Textarea,
} from '@fluentui/react-components';

import type { Grader } from '../../../hooks/useGraders/useGraders.interface.js';
import { graderTypeLabel } from '../../../services/grader.interface.js';
import { useStyles } from '../GraderPage.style.js';

import type { OverviewSectionsProps } from './graderType.types.js';

/** Flattens a spec sub-object into read-only `[key, displayValue]` pairs. */
function toEntries(value: unknown): [string, string][] {
  if (value == null || typeof value !== 'object') return [];
  return Object.entries(value as Record<string, unknown>).map(([key, raw]) => [
    key,
    typeof raw === 'string' ? raw : JSON.stringify(raw),
  ]);
}

function ConfigGroup({
  title,
  entries,
}: {
  title: string;
  entries: [string, string][];
}) {
  const styles = useStyles();
  if (entries.length === 0) return null;
  return (
    <section className={styles.overviewCard} aria-label={title}>
      <div className={styles.overviewCardTitle}>{title}</div>
      <div className={styles.overviewCardFields}>
        {entries.map(([key, value]) => (
          <Field key={key} label={key}>
            <Input className={styles.fullWidthControl} value={value} disabled />
          </Field>
        ))}
      </div>
    </section>
  );
}

/** Top-of-Overview info notice for an unmodelled grader type. */
export function FallbackOverviewNotice({ judge }: { judge: Grader }) {
  return (
    <MessageBar intent="info">
      <MessageBarBody>
        The <strong>{graderTypeLabel(judge.graderType)}</strong> grader type
        isn&apos;t fully supported in this UI yet. Its saved configuration is
        shown below read-only.
      </MessageBarBody>
    </MessageBar>
  );
}

export function FallbackOverviewSections({ judge }: OverviewSectionsProps) {
  const styles = useStyles();
  const spec = judge.rawSpec as
    | { init?: unknown; invoke?: unknown }
    | undefined;

  const initEntries = toEntries(spec?.init);
  const invokeEntries = toEntries(spec?.invoke);
  const hasStructured = initEntries.length > 0 || invokeEntries.length > 0;

  if (hasStructured) {
    return (
      <>
        <ConfigGroup title="Init" entries={initEntries} />
        <ConfigGroup title="Invoke" entries={invokeEntries} />
      </>
    );
  }

  if (judge.rawSpec == null) return null;

  return (
    <section className={styles.overviewCard} aria-label="Configuration">
      <div className={styles.overviewCardTitle}>Configuration</div>
      <div className={styles.overviewCardFields}>
        <Field label="Raw spec">
          <Textarea
            className={styles.textareaMonospace}
            value={JSON.stringify(judge.rawSpec, null, 2)}
            resize="vertical"
            rows={8}
            disabled
          />
        </Field>
      </div>
    </section>
  );
}
